/**
 * useBrandingText: resolve the active-language version of a tenant
 * branding string (product_name, strapline, attribution_label,
 * attribution_name).
 *
 * Why a separate hook from useT():
 *   - useT() reads from the platform_ui_i18n catalogue (chrome
 *     strings translatable through Site Vocabulary).
 *   - Branding strings live on the tenant's branding object and
 *     are not in the StringKey union (each tenant authors their
 *     own English on the Branding page; there's no platform
 *     default).
 *   - So branding gets its own resolver that pulls from
 *     `me.current_client.branding.text_overrides[lang][field]` with
 *     a fallback to the English value on the same branding object.
 *
 * Active language follows the dashboard's I18nProvider, same code
 * useT() resolves against. That means flipping the dashboard
 * language updates branding strings the same way it updates chrome.
 *
 * Returns '' if /me hasn't arrived yet OR the tenant hasn't
 * authored the English value either. Callers add any product-
 * default fallback themselves ('Locksmith', etc).
 */
import { useMe } from '../auth/MeContext';
import { useI18nLang } from '../I18nContext';

export type BrandingTextField =
  | 'product_name'
  | 'strapline'
  | 'attribution_label'
  | 'attribution_name';

export function useBrandingText(field: BrandingTextField): string {
  const { me } = useMe();
  const lang = useI18nLang();
  const branding = me?.current_client?.branding;
  if (!branding) return '';

  const override = branding.text_overrides?.[lang]?.[field];
  if (override && override.trim()) return override;

  // Fall back to the English version (the tenant's authored value
  // on the Branding page). Empty when the field is blank in both
  // the override map AND on the branding object.
  return (branding[field] || '').toString();
}
